import type { AnyObject, IErrorResponse, ISuccessResponse } from "../interfaces";
import StorageService from "../services/storage-service";
import { secondsToMilliseconds } from "./date";
import { isServerDownError  } from "./http";
import logger from "./logger";

interface ICachedRequestOptions {
  cacheKey: string;  // If the function takes arguments, we'll generate the cache key from the arguments if key is not given.
  expiresIn?: number; // the number of seconds to cache the response, default is 300 seconds (5 minutes)
  isSuccessfulRequest?: (data) => boolean; // indicates that the request is successful and should be cached.
}

interface ICacheData {
  data: AnyObject;
  expiry: number;
}

interface ICachedRequestResponse { 
  data: AnyObject | null; 
  clearCachedResponse: () => void;
}

/*
 * VIP Note: This function is deprecated because its use is complicated. 
 * Use the `withCachedResponse` instead which is simpler to use. 
 * Over time, replace every use of this with `withCachedResponse` 
 * and then delete this function.
 */
export default async function makeCachedRequest(request: () => Promise<ISuccessResponse | IErrorResponse>, options: ICachedRequestOptions): Promise<ICachedRequestResponse> {
  const { cacheKey: key, expiresIn: secondsToExpire = 300 } = options ?? {};

  let data: AnyObject | null = null;
  const msToExpire = secondsToMilliseconds(secondsToExpire);
  const cachedItem = StorageService.getItem(key);

  try {
    const cache: ICacheData | null = cachedItem ?? null;

    if(cache?.data && (cache?.expiry > Date.now())) {
      data = cache.data;
    } else {
      // Clear any previously cached but expired response(s) 
      // so that we don't fill the local storage too fast
      clearCachedResponse(); 

      const response = await request();

      if(response && "success" in response) {
        data = (response as ISuccessResponse).data;

        StorageService.setItem(key, {
          data,
          expiry: (Date.now() + msToExpire)
        });
      } else if(response && "error" in response) {
        logger.error(
          `helpers::makeCachedRequest: API returned error response:`,
          (response as IErrorResponse).errorData.message
        );
      }
    }
  } catch(error) {
    logger.error("helpers::makeCachedRequest: Error", error);

    if(isServerDownError(error)) {
      throw error;
    }
  }

  return { data, clearCachedResponse };

  function clearCachedResponse() {
    if(StorageService.getItem(key)) {
      StorageService.removeItem(key);
    }
  }
}

/**
 * Wrap an async function such that after initial fetch, 
 * the next fetch returns cached data.
 * The wrapped function is also augmented with a `clearCache` method 
 * that can be invoked to clear the cache before its stipulated expiry. 
 */
export function withCachedResponse<T>(callback: (...args) => Promise<T | null>, options: Partial<ICachedRequestOptions>) {
  const { cacheKey = "", expiresIn: secondsToExpire = 300, isSuccessfulRequest = () => true } = options ?? {};

  interface ICacheData {
    data: T;
    expiry: number;
  };

  let data: T | null = null;
  const msToExpire = secondsToMilliseconds(secondsToExpire);
  //const cachedItem = StorageService.getItem(key);

  function sort(object: object) {
    const sortedEntries = Object.entries(object).sort((a, b) =>
      a[0].localeCompare(b[0])
    );
  
    const withSortedValues = sortedEntries.map(([key, value]) => {
      const isObject = typeof value === 'object' && value !== null
      const newVal = isObject ? sort(value) : value;
      return [key, newVal];
    });
  
    return Object.fromEntries(withSortedValues);
  }

  function replacer(k, v) {
    return v === undefined ? "[undefined]" : v;
  }
  
  function getKey(obj: object) {
    return JSON.stringify(sort(obj), replacer);
  }

  return async function wrapped(...args): Promise<T | null> { 
    let key = cacheKey;

    if(args.length > 0) {
      if(key.length > 0) {
        key = `${key}_${getKey(args)}`
      } else {
        key = getKey(args);
      }
    }
    
    const cachedItem = StorageService.getItem(key);
    const cache: ICacheData | null = cachedItem ?? null;

    function clearCachedResponse() {
      if(StorageService.getItem(key)) {
        StorageService.removeItem(key);
      }
    }

    /* @ts-expect-error: don't check for TS error */
    if(typeof wrapped.clearCache !== "function") { 
      /* @ts-expect-error: don't check for TS error */
      wrapped.clearCache = clearCachedResponse;
    }

    if(cache?.data && (cache?.expiry > Date.now())) {
      data = cache.data;
    }  else {
      // Clear any previously cached but expired response(s) 
      // so that we don't fill the local storage too fast
      clearCachedResponse(); 

      data = await callback(...args);

      // If `cacheOnlyOnSuccess` is false, then cache whatever result is returned.
      // However, if `cacheOnlyOnSuccess` is true, then cache only a successful request.
      if(isSuccessfulRequest(data)) { 
        StorageService.setItem(key, {
          data,
          expiry: (Date.now() + msToExpire)
        });
      }
    }

    return data;
  }
}
